import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { Observable } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	startWith,
	switchMap,
} from 'rxjs/operators';
import {
	billingEndDateOptions,
	billingLabels,
	billingTriggerOptions,
	currencyTypeOptions,
	mrcOrNrcOptions,
	unitsOfMeasureOptions,
} from 'src/app/models/billingContractAndOptions';
import { Company } from 'src/app/models/data/company';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatOption, MatSelect } from '@angular/material/select';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepickerToggleIcon,
	MatDatepicker,
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';

@Component({
	selector: 'app-billing-add-contract',
	templateUrl: './billing-add-contract.component.html',
	styleUrls: ['./billing-add-contract.component.scss'],
	imports: [
		MatDivider,
		FormsModule,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatAutocompleteTrigger,
		MatAutocomplete,
		MatOption,
		MatSelect,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatIcon,
		MatDatepickerToggleIcon,
		MatDatepicker,
		MatSlideToggle,
		MatRadioGroup,
		MatRadioButton,
		NgClass,
		MatButton,
		AsyncPipe,
	],
})
export class BillingAddContractComponent implements OnInit {
	@Input() savedAddContractFormGroup?: FormGroup;
	@Input() savedAddOnBillingFormGroup?: FormGroup;
	@Output() continueContractRegistration = new EventEmitter();

	filteredCompanyOptions?: Observable<Company[]>;
	selectedCompany?: Company;
	minBillingStartDate: Date = new Date(new Date().setHours(0, 0, 0, 0));

	filterLabels = billingLabels;
	currencyTypeOptions = currencyTypeOptions;
	mrcOrNrcOptions = mrcOrNrcOptions;
	unitsOfMeasureOptions = unitsOfMeasureOptions;
	billingTriggerOptions = billingTriggerOptions;
	billingEndDateOptions = billingEndDateOptions;

	addContractFormGroup: FormGroup;
	addOnBillingFormGroup: FormGroup;

	constructor(
		private apiService: ApiService,
		private fb: FormBuilder
	) {
		this.addContractFormGroup = this.fb.group({
			company: [null, [Validators.required, this.selectedCompanyValidator]],
			netsuiteCustomerInternalId: [
				'',
				[Validators.required, this.noWhitespaceValidator],
			],
			contractName: ['', [Validators.required, this.noWhitespaceValidator]],
			contractId: ['', [Validators.required, this.noWhitespaceValidator]],
			unitOfMeasure: ['', [Validators.required]],
			currencyType: [null, [Validators.required]],
			unitPrice: [
				'',
				[
					Validators.required,
					this.noWhitespaceValidator,
					Validators.pattern(
						/^\s*[+-]?(\d+|\d*\.\d+|\d+\.\d*)([Ee][+-]?\d+)?\s*$/
					),
				],
			],
			billingStartDate: [
				'',
				[Validators.required, this.dateGreaterThanTodayValidator],
			],
			billingTrigger: [null, [Validators.required]],
			durationInMonths: [
				'',
				[
					Validators.required,
					this.noWhitespaceValidator,
					Validators.pattern(/^\s*\d*\s*$/),
				],
			],
			annualIncToggle: [false, [Validators.required]],
			annualIncAdjustmentRate: ['', []],
			deviceLeaseToggle: [false, [Validators.required]],
			autoRenewToggle: [false, [Validators.required]],
		});
		this.addOnBillingFormGroup = this.fb.group({
			addOnBillingFormArray: this.fb.array([]),
		});
		this.addAddOnBillingOption();
	}

	ngOnInit(): void {
		if (this.savedAddContractFormGroup) {
			this.addContractFormGroup = this.savedAddContractFormGroup;
			this.selectedCompany =
				this.savedAddContractFormGroup.get('company')?.value;
		}
		if (this.savedAddOnBillingFormGroup) {
			this.addOnBillingFormGroup = this.savedAddOnBillingFormGroup;
		}

		this.filteredCompanyOptions = this.addContractFormGroup
			.get('company')
			?.valueChanges.pipe(
				startWith(''),
				debounceTime(300),
				distinctUntilChanged(),
				switchMap((keyword) => this.searchCompanies(keyword))
			);

		this.updateAnnualIncAdjustmentRateFormControl();
		this.addContractFormGroup
			.get('annualIncToggle')
			?.valueChanges.subscribe((value) => {
				this.updateAnnualIncAdjustmentRateFormControl();
			});

		this.addContractFormGroup.valueChanges.subscribe((changes) => {
			this.isFormValid();
		});
		this.addOnBillingFormGroup.valueChanges.subscribe((changes) => {
			this.isFormValid();
		});
	}

	noWhitespaceValidator(control: FormControl) {
		return (control.value || '').trim().length ? null : { whitespace: true };
	}

	selectedCompanyValidator(control: FormControl) {
		return typeof control.value === 'object' ? null : { whitespace: true };
	}

	dateGreaterThanTodayValidator(control: FormControl) {
		// Check if valid date
		if (!control.value || typeof control.value.getMonth !== 'function') {
			return { InvalidDate: true };
		}

		// Check if the date the user typed in is in the past
		const today = new Date(new Date().setHours(0, 0, 0, 0));
		if (new Date(control.value) < today) {
			return { InvalidDate: true };
		}
		return null;
	}

	isFormValid() {
		// Check if everything else is valid before add-on billing settings
		if (!this.addContractFormGroup.valid) {
			this.continueContractRegistration.emit({
				addContractFormGroup: null,
				addOnBillingFormGroup: null,
			});
			return false;
		}

		// Check if add-on billing controls valid
		if (
			(this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray)
				?.controls
		) {
			for (const control of (
				this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray
			)?.controls) {
				if (!this.allControlsAreBlankOrValidInAddOnBilling(control)) {
					this.continueContractRegistration.emit({
						addContractFormGroup: null,
						addOnBillingFormGroup: null,
					});
					return false;
				}
			}
		}

		this.continueContractRegistration.emit({
			addContractFormGroup: this.addContractFormGroup,
			addOnBillingFormGroup: this.addOnBillingFormGroup,
		});
		return true;
	}

	async searchCompanies(keyword: string) {
		const queryParameters: QueryParam[] = [
			{ param: 'companyName', value: keyword },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
			{ param: 'active', value: true },
		];
		return this.apiService.searchCompaniesPublicDirectory(queryParameters);
	}

	displayCompanyName = (): string => {
		return this.selectedCompany ? this.selectedCompany.name : '';
	};

	onCompanySelected(event: any) {
		this.selectedCompany = event.source.value;
		this.addContractFormGroup
			.get('company')
			?.setValue(
				this.selectedCompany && this.selectedCompany.name
					? this.selectedCompany.name
					: ''
			);
	}

	onCompanyNameTextChange(event: any, trigger: any) {
		if (event.target.value === this.selectedCompany?.name) {
			trigger.closePanel();
		} else {
			this.selectedCompany = undefined;
			trigger.openPanel();
		}
	}

	updateAnnualIncAdjustmentRateFormControl() {
		const checked = this.addContractFormGroup.get('annualIncToggle')?.value;
		// Limit this increase field to numbers with decimals and don't allow over 3 digits in this field
		const validators = [
			Validators.required,
			Validators.pattern(/^\s*[+-]?(\d+|\d*\.\d+|\d+\.\d*)([Ee][+-]?\d+)?\s*$/),
			Validators.pattern(/^\s*.{0,3}\s*$/),
		];
		if (checked) {
			this.addContractFormGroup.get('annualIncAdjustmentRate')?.enable();
			this.addContractFormGroup
				.get('annualIncAdjustmentRate')
				?.addValidators(validators);
			this.addContractFormGroup
				.get('annualIncAdjustmentRate')
				?.updateValueAndValidity();
		} else {
			this.addContractFormGroup.get('annualIncAdjustmentRate')?.disable();
			this.addContractFormGroup
				.get('annualIncAdjustmentRate')
				?.clearValidators();
			this.addContractFormGroup
				.get('annualIncAdjustmentRate')
				?.updateValueAndValidity();
		}
	}

	getControls() {
		if (this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray) {
			return (
				this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray
			).controls;
		}
		return [] as AbstractControl<any, any>[];
	}

	isValidControlForOnBillingFormGroup(
		group: AbstractControl<any, any>,
		controlName: string
	) {
		if (group instanceof FormGroup) {
			if ((group as FormGroup).controls[controlName].valid) {
				return true;
			}

			// If other controls aren't blank and this one is make it invalid
			for (const key of Object.keys(group.controls)) {
				if (key !== controlName) {
					const control = group.get(key);
					if (control) {
						if (
							(key !== 'recurringChargeTypeId' &&
								control.value.trim().length) ||
							(key === 'recurringChargeTypeId' && control.value !== null)
						) {
							return false;
						}
					}
				}
			}
		}
		return true;
	}

	addAddOnBillingOption(event?: any) {
		if (event) {
			event.preventDefault();
		}
		const newAddOnBillingFormControl = this.fb.group({
			description: [
				'',
				[
					Validators.required,
					this.noWhitespaceValidator,
					Validators.maxLength(250),
				],
			],
			quantity: ['', [Validators.required, Validators.pattern(/^\s*\d*\s*$/)]],
			unitPrice: [
				'',
				[
					Validators.required,
					Validators.pattern(
						/^\s*[+-]?(\d+|\d*\.\d+|\d+\.\d*)([Ee][+-]?\d+)?\s*$/
					),
				],
			],
			recurringChargeTypeId: [null, [Validators.required]],
		});

		(this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray).push(
			newAddOnBillingFormControl
		);
		this.addOnBillingFormGroup
			.get('addOnBillingFormArray')
			?.updateValueAndValidity();
	}

	allControlsAreBlankOrValidInAddOnBilling(group: AbstractControl) {
		// If all of the form controls in the form group are blank or are valid, then the form group is valid
		if (group instanceof FormGroup) {
			let allAreBlank = true;
			let allAreValid = true;
			for (const key of Object.keys(group.controls)) {
				const control = group.get(key);
				if (control) {
					if (control.invalid) {
						allAreValid = false;
					}
					if (
						(key !== 'recurringChargeTypeId' && control.value.trim().length) ||
						(key === 'recurringChargeTypeId' && control.value !== null)
					) {
						allAreBlank = false;
					}
				}
			}

			if (allAreBlank || allAreValid) {
				return true;
			}
			return false;
		}
		return true;
	}

	removeAddOnBillingOption(i: number) {
		(
			this.addOnBillingFormGroup.get('addOnBillingFormArray') as FormArray
		).removeAt(i);
	}

	isNumberKey(event: any) {
		const charCode = event.which ? event.which : event.keyCode;
		if (charCode > 31 && (charCode < 48 || charCode > 57)) {
			return false;
		}
		return true;
	}

	isNumberOrPeriodKey(event: any) {
		const charCode = event.which ? event.which : event.keyCode;
		if (charCode > 31 && charCode != 46 && (charCode < 48 || charCode > 57)) {
			return false;
		}
		return true;
	}
}
